/*
 * https://usehooks-typescript.com/react-hook/use-on-click-outside
 * React hook for listening for clicks outside of a specified element.
 * This can be useful for closing a modal, a dropdown menu etc.
 */
import { RefObject, useEffect } from 'react';

type AnyEvent = MouseEvent | TouchEvent | KeyboardEvent;

function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: AnyEvent) => void, 
  hideOnTab?: boolean,
): void {
  useEffect(() => {
    const listener = (event: AnyEvent) => {
      const el = ref?.current;

      if (event instanceof KeyboardEvent) {
        // Handle Escape key to close the popup
        if (event.key === 'Escape') {
          handler(event);
        }

        if (hideOnTab && event.key === 'Tab') {
          handler(event);
        }
        return;
      }

      // Do nothing if clicking inside the ref's element or its descendants
      if (!el || el.contains(event.target as Node)) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    document.addEventListener('keydown', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
      document.removeEventListener('keydown', listener);
    };
  }, [ref, handler]);
}

export default useOnClickOutside;


